import Profile from "../../assets/img/bprlogo1.png";
import { IoMdClose } from "react-icons/io";
import { useEffect, useRef, useState } from "react";
import { TbMessageChatbot, TbHeadset } from "react-icons/tb";
import { FaPaperPlane, FaWhatsapp } from "react-icons/fa";
import { MdAccountBalance, MdHelpOutline } from "react-icons/md";
import { LazyLoadImage } from "react-lazy-load-image-component";
import axios from "axios";

type Message = {
    id: number;
    type: "bot" | "user";
    message: string;
    time: Date;
    actions?: {
        label: string;
        url: string;
    }[];
};

export function ModalChat() {
    const [openChat, setOpenChat] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [showIntro, setShowIntro] = useState(false);
    const [displayIntroText, setDisplayIntroText] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const bottomRef = useRef<HTMLDivElement | null>(null);

    const introText =
        "Saya adalah asisten virtual BPR Palu Anugerah. Pilih topik di bawah atau ketik pertanyaan Anda.";

    const [messages, setMessages] = useState<Message[]>([
        {
            id: 1,
            type: "bot",
            message:
                "Selamat datang di BPR Palu Anugerah! Silakan tanyakan seputar produk kredit, tabungan, atau layanan kami.",
            time: new Date(),
        },
    ]);

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    useEffect(() => {
        bottomRef.current?.scrollIntoView({
            behavior: "smooth",
        });
    }, [messages, openChat]);

    useEffect(() => {
        if (!openChat) return;

        setShowIntro(false);
        setDisplayIntroText("");

        let typingInterval: ReturnType<typeof setInterval>;

        const introTimer = setTimeout(() => {
            setShowIntro(true);

            let index = 0;

            typingInterval = setInterval(() => {
                setDisplayIntroText(
                    introText.slice(0, index + 1)
                );

                index++;

                if (index >= introText.length) {
                    clearInterval(typingInterval);
                }
            }, 28);
        }, 250);

        return () => {
            clearTimeout(introTimer);
            clearInterval(typingInterval);
        };
    }, [openChat]);

    const handleQuickQuestion = (text: string) => {
        if (isLoading) return;

        sendMessage(text);
    };

    const handleSendMessage = () => {
        if (isLoading) return;

        sendMessage(inputValue);
    };

    const sendMessage = (value: string) => {
        const text = value.trim();

        if (!text || isLoading) return;

        const now = new Date();
        const userMessageId = Date.now();
        const processingId = userMessageId + 1;

        setMessages((prev) => [
            ...prev,
            {
                id: userMessageId,
                type: "user",
                message: text,
                time: now,
            },
            {
                id: processingId,
                type: "bot",
                message: "AI sedang memproses",
                time: new Date(),
            },
        ]);

        setInputValue("");
        setIsLoading(true);

        processInput(text, processingId);
    };

    const handleKeyEnter = (
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (e.key === "Enter") {
            e.preventDefault();

            handleSendMessage();
        }
    };

    const processInput = async (
        input: string,
        processingId: number
    ) => {
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/ai/chat`,
                {
                    message: input,
                }
            );

            setMessages((prev) => {
                const withoutProcessing = prev.filter(
                    (msg) => msg.id !== processingId
                );

                return [
                    ...withoutProcessing,
                    {
                        id: Date.now(),
                        type: "bot",
                        message: data.response,
                        time: new Date(),
                        actions: data.hubungi_petugas
                            ? [
                                  {
                                      label: "Hubungi Petugas",
                                      url: "https://wa.me/6282290333669",
                                  },
                              ]
                            : [],
                    },
                ];
            });
        } catch (error) {
            console.error("AI Chat Error:", error);

            let errorMessage =
                "Maaf, layanan AI sedang tidak tersedia. Silakan coba beberapa saat lagi.";

            if (axios.isAxiosError(error)) {
                if (error.response?.status === 429) {
                    errorMessage =
                        "Anda terdeteksi mengirim terlalu banyak pesan. Silakan tunggu beberapa menit sebelum mencoba kembali.";
                }
            }

            setMessages((prev) => {
                const withoutProcessing = prev.filter(
                    (msg) => msg.id !== processingId
                );

                return [
                    ...withoutProcessing,
                    {
                        id: Date.now(),
                        type: "bot",
                        message: errorMessage,
                        time: new Date(),
                    },
                ];
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {openChat && (
                <div className="fixed z-50 right-2 bottom-3 md:right-6 md:bottom-6 w-[calc(100vw-16px)] md:w-[460px] h-[calc(100vh-24px)] md:h-[760px] max-h-[900px] animate-[chatOpen_0.35s_ease-out]">
                    <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col h-full overflow-hidden border">

                        <div className="bg-white dark:bg-slate-700 px-4 py-3 border-b flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <div className="bg-blue-600 text-white h-10 w-10 rounded-full flex items-center justify-center">
                                        <TbHeadset className="text-2xl" />
                                    </div>

                                    <span className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-white" />
                                </div>

                                <div>
                                    <p className="font-bold text-blue-700 leading-tight">
                                        AI Customer Service
                                    </p>

                                    <p className="text-xs text-green-600">
                                        ● Online 24/7
                                    </p>
                                </div>
                            </div>

                            <button
                                className="hover:bg-gray-100 dark:hover:bg-slate-600 rounded-md p-1"
                                onClick={() =>
                                    setOpenChat(false)
                                }
                            >
                                <IoMdClose className="text-xl text-slate-700 dark:text-white" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">

                            <div
                                className={`text-center py-6 transition-all duration-700 ${
                                    showIntro
                                        ? "opacity-100 translate-y-0"
                                        : "opacity-0 translate-y-4"
                                }`}
                            >
                                <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center animate-bounce">
                                    <TbHeadset className="text-5xl text-blue-700" />
                                </div>

                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    Halo, ada yang bisa saya bantu hari ini?
                                </h2>

                                <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 max-w-xs mx-auto min-h-[42px]">
                                    {displayIntroText}

                                    <span className="animate-pulse">
                                        |
                                    </span>
                                </p>

                                <div className="flex flex-wrap justify-center gap-2 mt-5">

                                    <button
                                        disabled={isLoading}
                                        onClick={() =>
                                            handleQuickQuestion(
                                                "Saya ingin bertanya tentang info kredit"
                                            )
                                        }
                                        className="px-4 py-2 bg-white border rounded-full text-sm hover:bg-blue-50 flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <MdAccountBalance />
                                        Info Kredit
                                    </button>

                                    <button
                                        disabled={isLoading}
                                        onClick={() =>
                                            handleQuickQuestion(
                                                "Saya ingin membuka tabungan"
                                            )
                                        }
                                        className="px-4 py-2 bg-white border rounded-full text-sm hover:bg-blue-50 flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <MdAccountBalance />
                                        Buka Tabungan
                                    </button>

                                    <button
                                        disabled={isLoading}
                                        onClick={() =>
                                            handleQuickQuestion(
                                                "Saya butuh bantuan lain"
                                            )
                                        }
                                        className="px-4 py-2 bg-white border rounded-full text-sm hover:bg-blue-50 flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <MdHelpOutline />
                                        Bantuan Lain
                                    </button>

                                </div>
                            </div>

                            <div className="flex flex-col space-y-3">

                                {messages.map((message) => (
                                    <div
                                        key={message.id}
                                        className={`flex ${
                                            message.type === "bot"
                                                ? "items-start"
                                                : "justify-end"
                                        }`}
                                    >
                                        {message.type === "bot" && (
                                            <div className="mr-2 flex-shrink-0">
                                                <LazyLoadImage
                                                    src={Profile}
                                                    alt="bot profile"
                                                    width={36}
                                                    height={36}
                                                    className="rounded-full h-9 w-9 object-cover bg-blue-600"
                                                />
                                            </div>
                                        )}

                                        <div className="max-w-[78%]">

                                            <div
                                                className={`p-3 rounded-2xl text-sm break-words shadow-sm ${
                                                    message.type === "bot"
                                                        ? "bg-white dark:bg-slate-700 text-gray-800 dark:text-white rounded-tl-none border"
                                                        : "bg-blue-600 text-white rounded-tr-none"
                                                }`}
                                            >
                                                {message.message ===
                                                "AI sedang memproses" ? (
                                                    <div className="flex items-center">
                                                        <span>
                                                            AI sedang memproses
                                                        </span>

                                                        <span className="flex gap-1 ml-2">
                                                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]" />

                                                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]" />

                                                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                                                        </span>
                                                    </div>
                                                ) : (
                                                    message.message
                                                )}
                                            </div>

                                            <p
                                                className={`text-[10px] text-slate-500 mt-1 ${
                                                    message.type === "bot"
                                                        ? "ml-1"
                                                        : "text-right mr-1"
                                                }`}
                                            >
                                                {formatTime(
                                                    message.time
                                                )}
                                            </p>

                                            {message.actions &&
                                                message.actions.length >
                                                    0 && (
                                                    <div className="mt-2 flex flex-col gap-2">
                                                        {message.actions.map(
                                                            (
                                                                action,
                                                                index
                                                            ) => (
                                                                <a
                                                                    key={
                                                                        index
                                                                    }
                                                                    href={
                                                                        action.url
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center justify-center gap-2 bg-green-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-green-700 transition"
                                                                >
                                                                    <FaWhatsapp />

                                                                    {
                                                                        action.label
                                                                    }
                                                                </a>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                ))}

                                <div ref={bottomRef} />
                            </div>
                        </div>

                        <div className="bg-white dark:bg-slate-700 p-4 border-t">
                            <div className="flex items-center gap-2">

                                <input
                                    type="text"
                                    placeholder="Tulis pertanyaan Anda..."
                                    value={inputValue}
                                    disabled={isLoading}
                                    className="flex-1 border rounded-full px-4 py-3 text-sm focus:outline-none dark:bg-slate-800 dark:text-white disabled:opacity-60"
                                    onChange={(e) =>
                                        setInputValue(
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={handleKeyEnter}
                                />

                                <button
                                    onClick={handleSendMessage}
                                    disabled={
                                        isLoading ||
                                        !inputValue.trim()
                                    }
                                    className="bg-blue-600 text-white rounded-full p-3 hover:bg-blue-700 focus:outline-none transition disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <FaPaperPlane />
                                </button>

                            </div>
                        </div>

                    </div>
                </div>
            )}

            {!openChat && (
                <div className="fixed z-50 bottom-4 right-4">
                    <button
                        onClick={() => setOpenChat(true)}
                        className="bg-blue-600 text-white shadow-2xl rounded-full p-4 hover:bg-blue-700 transition"
                    >
                        <TbMessageChatbot className="text-3xl" />
                    </button>
                </div>
            )}
        </>
    );
}